import React, { useRef, useState, useEffect } from "react";
import { FaArrowRight } from "react-icons/fa";
import { FaMapMarkerAlt, FaPhoneAlt, FaClock, FaEnvelope } from "react-icons/fa";

const Contact = ({ onOpenModal }) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", bike: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 3500);
    setForm({ name: "", phone: "", bike: "", message: "" });
  };

  const infos = [
    { icon: <FaMapMarkerAlt />, label: "Address", value: "Sri Kalikambal Autos, Katpadi, Vellore, Tamil Nadu" },
    { icon: <FaPhoneAlt />, label: "Phone", value: "+91 88797 37412" },
    { icon: <FaClock />, label: "Hours", value: "Mon – Sat: 9:00 AM – 7:00 PM" },
    { icon: <FaEnvelope />, label: "Email", value: "info@srikalikambalautos.in" },
  ];

  return (
    <>
      <style>{`
        /* ── FONT CHANGE: Oswald (display) + DM Sans (body) ── */
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&family=Barlow+Condensed:wght@400;600;700;800&display=swap');

        .font-bebas       { font-family: 'Oswald', sans-serif; letter-spacing: 0.02em; }
        .font-barlow      { font-family: 'DM Sans', sans-serif; }
        .font-barlow-cond { font-family: 'Barlow Condensed', sans-serif; }

        .contact-grid-bg {
          background-image:
            linear-gradient(rgba(212,175,55,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212,175,55,0.03) 1px, transparent 1px);
          background-size: 56px 56px;
        }

        /* Input */
        .contact-input {
          background: #0d0d0d;
          border: 1px solid rgba(255,255,255,0.07);
          color: #F5F5F5;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          width: 100%;
          padding: 12px 16px;
          outline: none;
          transition: border-color 0.3s, box-shadow 0.3s;
          clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px));
        }
        .contact-input::placeholder { color: #555; }
        .contact-input:focus {
          border-color: #D4AF37;
          box-shadow: 0 0 0 1px rgba(212,175,55,0.25);
        }

        select.contact-input option { background: #111111111; color: #F5F5F5; }

        .submit-btn {
          clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%);
          transition: background 0.3s, transform 0.2s;
        }
        .submit-btn:hover { background: #E5C158 !important; }
        .submit-btn:active { transform: scale(0.98); }

        .map-clip {
          clip-path: polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px));
          overflow: hidden;
        }

        .info-card {
          border-left: 3px solid #D4AF37;
          background: #111111111;
          border-top: 1px solid rgba(255,255,255,0.04);
          border-right: 1px solid rgba(255,255,255,0.04);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          transition: background 0.3s, transform 0.3s;
        }
        .info-card:hover {
          background: rgba(212,175,55,0.06);
          transform: translateX(4px);
        }

        .form-panel {
          background: #111111111;
          border: 1px solid rgba(255,255,255,0.05);
          clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 22px 100%, 0 calc(100% - 22px));
          position: relative;
        }
        .form-panel::before {
          content:'';
          position:absolute; top:0; right:0;
          border-style:solid;
          border-width: 0 38px 38px 0;
          border-color: transparent #D4AF37 transparent transparent;
          z-index:2;
        }

        .c-reveal {
          opacity: 0; transform: translateY(36px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .c-reveal.shown { opacity: 1; transform: translateY(0); }

        .contact-watermark {
          font-family: 'Oswald', sans-serif;
          font-size: clamp(60px, 12vw, 160px);
          color: rgba(212,175,55,0.04);
          letter-spacing: -3px;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }

        @keyframes flashIn {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .success-msg { animation: flashIn 0.4s ease forwards; }

        .field-label {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 11px; letter-spacing: 3px;
          text-transform: uppercase; color: #666;
          margin-bottom: 6px; display: block;
        }
      `}</style>

      <section ref={sectionRef} id="contact" className="relative w-full bg-[#050505] py-24 overflow-hidden">
        <div className="contact-grid-bg absolute inset-0 z-0 pointer-events-none" />
        <div className="contact-watermark absolute bottom-6 left-0 z-0 select-none">CONTACT</div>
        <div className="absolute right-0 top-1/4 bottom-1/4 w-[3px] bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent opacity-40" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">

          {/* HEADER */}
          <div className={`c-reveal ${visible ? "shown" : ""} mb-16`}>
            <div className="flex items-center gap-3 font-barlow-cond text-[11px] tracking-[4px] uppercase text-[#D4AF37] mb-4">
              <span className="w-8 h-px bg-[#D4AF37]" />
              Get In Touch
            </div>
            <h2 className="font-bebas leading-[0.9]" style={{ fontSize: "clamp(48px, 7vw, 90px)" }}>
              <span className="text-[#F5F5F5]">Visit </span>
              <span className="text-[#D4AF37]">Our </span>
              <span style={{ WebkitTextStroke: "1.5px #F5F5F5", color: "transparent" }}>Showroom</span>
            </h2>
          </div>

          {/* MAIN GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

            {/* LEFT — FORM */}
            <div className={`c-reveal ${visible ? "shown" : ""} flex flex-col gap-6`} style={{ transitionDelay: "0.1s" }}>

              {/* Info cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {infos.map((info) => (
                  <div key={info.label} className="info-card flex items-start gap-3 px-4 py-3">
                    <span className="text-[18px] text-[#D4AF37] mt-0.5 flex-shrink-0">{info.icon}</span>
                    <div>
                      <div className="font-barlow-cond text-[10px] tracking-[3px] uppercase text-[#D4AF37] mb-0.5">{info.label}</div>
                      {/* Info value — 14px, brighter */}
                      <div className="font-barlow text-[14px] text-[#9CA3AF] leading-snug">{info.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Form panel */}
              <div className="form-panel p-7">
                <p className="font-barlow-cond text-[11px] tracking-[4px] uppercase text-[#D4AF37] mb-6">Send a Message</p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Your Name</label>
                      <input
                        name="name" value={form.name} onChange={handleChange}
                        required placeholder="Ravi Kumar" className="contact-input"
                      />
                    </div>
                    <div>
                      <label className="field-label">Phone Number</label>
                      <input
                        name="phone" value={form.phone} onChange={handleChange}
                        required placeholder="+91 88797 37412" className="contact-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label">Interested In</label>
                    <select name="bike" value={form.bike} onChange={handleChange} className="contact-input">
                      <option value="" disabled>Select a model</option>
                      <option>Bullet 350</option>
                      <option>Hunter 350</option>
                      <option>Meteor 350</option>
                      <option>Super Meteor 450</option>
                      <option>Continental GT 650</option>
                      <option>Interceptor 650</option>
                      <option>Himalayan</option>
                      <option>Just Enquiring</option>
                    </select>
                  </div>

                  <div>
                    <label className="field-label">Message</label>
                    <textarea
                      name="message" value={form.message} onChange={handleChange}
                      rows={4} placeholder="Tell us how we can help..."
                      className="contact-input resize-none"
                      style={{ clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))" }}
                    />
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <button type="submit" className="submit-btn flex items-center gap-3 bg-[#D4AF37] text-white pl-7 pr-5 py-3 font-barlow-cond text-[13px] font-bold tracking-[2px] uppercase">
                      Send Message
                      <span className="text-base"><FaArrowRight /></span>
                    </button>
                    {sent && (
                      <span className="success-msg font-barlow-cond text-[12px] tracking-[2px] uppercase text-[#D4AF37]">
                        ✓ Message Sent!
                      </span>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* RIGHT — MAP */}
            <div className={`c-reveal ${visible ? "shown" : ""} flex flex-col gap-4`} style={{ transitionDelay: "0.2s" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 font-barlow-cond text-[11px] tracking-[4px] uppercase text-[#666]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" style={{ boxShadow: "0 0 6px #D4AF37" }} />
                  Sri Kalikambal Autos · Katpadi
                </div>
                <a
                  href="https://maps.google.com/?q=Royal+Enfield+Showroom+Sri+kalikambal+Autos"
                  target="_blank" rel="noreferrer"
                  className="font-barlow-cond text-[11px] flex tracking-[2px] uppercase text-[#D4AF37] hover:underline"
                >
                  Open Maps <span className="p-1"><FaArrowRight /></span>
                </a>
              </div>

              <div className="map-clip relative w-full border border-[#D4AF37]/20" style={{ height: 420 }}>
                <div
                  className="absolute top-0 right-0 z-10 pointer-events-none"
                  style={{ width: 0, height: 0, borderStyle: "solid", borderWidth: "0 36px 36px 0", borderColor: "transparent #D4AF37 transparent transparent" }}
                />
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31105.827473407942!2d79.11689102727105!3d12.957229464062056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bad389e7ff210b3%3A0xa469bfd31b6ccc5b!2sRoyal%20Enfield%20Showroom%20-%20Sri%20kalikambal%20Autos!5e0!3m2!1sen!2sin!4v1774142842642!5m2!1sen!2sin"
                  width="100%" height="100%"
                  style={{ border: 0, display: "block", filter: "invert(90%) hue-rotate(180deg) saturate(0.7)" }}
                  allowFullScreen="" loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sri Kalikambal Autos Location"
                />
              </div>

              {/* Quick action strip */}
              <div
                className="flex items-center justify-between gap-4 bg-[#111111] border border-[#D4AF37]/15 px-6 py-4"
                style={{ clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))" }}
              >
                <div>
                  <p className="font-barlow-cond text-[10px] tracking-[3px] uppercase text-[#666] mb-1">Ready to visit?</p>
                  <p className="font-bebas text-[22px] text-[#F5F5F5] leading-none">Book a Test Ride Today</p>
                </div>
                <button
                  className="flex font-barlow-cond text-[12px] tracking-[2px] uppercase text-white bg-[#D4AF37] hover:bg-[#E5C158] px-6 py-3 transition-colors duration-300"
                  style={{ clipPath: "polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)" }}
                >
                  Call Now <span className="p-1"><FaArrowRight /></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;